import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Package,
  Ruler,
  CheckCircle2,
  XCircle,
  DollarSign,
  Pencil,
  Trash2,
  Save,
  X,
  Send,
  MessageCircle,
  ShieldCheck,
  AlertTriangle,
  ImageIcon,
  Clock,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { adminApi, isAdminLoggedIn, verifyAdminToken } from "@/utils/auth";

const packages = [
  { name: "Male T-Shirt", price: 280 },
  { name: "Female T-Shirt", price: 280 },
  { name: "Short Sleeve Monokini", price: 320 },
  { name: "Long Sleeve Monokini", price: 320 },
  { name: "All Inclusive Male T-Shirt", price: 550 },
  { name: "All Inclusive Female T-Shirt", price: 550 },
  { name: "All Inclusive Short Sleeve Monokini", price: 550 },
  { name: "All Inclusive Long Sleeve Monokini", price: 550 },
];

const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL"];

export default function RegistrantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [registrant, setRegistrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amountPaid, setAmountPaid] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    contact: "",
    email: "",
    package: "",
    size: "",
  });
  const [saving, setSaving] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendingWhatsapp, setResendingWhatsapp] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [proofPreviewOpen, setProofPreviewOpen] = useState(false);
  const [proofUrl, setProofUrl] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAdminLoggedIn()) {
        navigate("/admin/login");
        return;
      }
      const valid = await verifyAdminToken();
      if (!valid) {
        navigate("/admin/login");
        return;
      }
      fetchRegistrant();
    };
    checkAuth();
  }, [id, navigate]);

  const fetchRegistrant = async () => {
    try {
      const response = await adminApi.get(`/registrants/${id}`);
      setRegistrant(response.data);
      setAmountPaid(response.data.amount_paid?.toString() || "");
      setEditForm({
        name: response.data.name,
        contact: response.data.contact,
        email: response.data.email,
        package: response.data.package,
        size: response.data.size,
      });
      // Fetch payment proof if it exists
      if (response.data.payment_proof_filename) {
        try {
          const proofRes = await adminApi.get(`/registrants/${id}/proof`, { responseType: "blob" });
          setProofUrl(URL.createObjectURL(proofRes.data));
        } catch (e) {
          // Proof unavailable; ignore
        }
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Registrant not found");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePayment = async (newStatus) => {
    try {
      const response = await adminApi.patch(`/registrants/${id}/payment`, {
        payment_status: newStatus,
        amount_paid: parseFloat(amountPaid) || 0
      });
      setRegistrant(response.data);
      toast.success(`Payment updated - $${amountPaid || 0} ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const markCollected = async () => {
    try {
      const response = await adminApi.patch(`/registrants/${id}/collect`);
      setRegistrant(response.data);
      toast.success("Marked as collected!");
    } catch (error) {
      toast.error("Failed to mark as collected");
    }
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const response = await adminApi.put(`/registrants/${id}`, editForm);
      setRegistrant(response.data);
      setIsEditing(false);
      toast.success("Changes saved successfully!");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditForm({
      name: registrant.name,
      contact: registrant.contact,
      email: registrant.email,
      package: registrant.package,
      size: registrant.size,
    });
    setIsEditing(false);
  };

  const deleteRegistrant = async () => {
    try {
      await adminApi.delete(`/registrants/${id}`);
      toast.success("Registrant deleted");
      navigate("/admin");
    } catch (error) {
      toast.error("Failed to delete registrant");
    }
  };

  const resendEmail = async () => {
    setResendingEmail(true);
    try {
      const response = await adminApi.post(`/registrants/${id}/resend-email`);
      toast.success(response.data.message || "Confirmation email sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend email");
    } finally {
      setResendingEmail(false);
    }
  };

  const resendWhatsapp = async () => {
    setResendingWhatsapp(true);
    try {
      const response = await adminApi.post(`/registrants/${id}/resend-whatsapp`);
      toast.success(response.data.message || "WhatsApp message sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send WhatsApp message");
    } finally {
      setResendingWhatsapp(false);
    }
  };

  const verifyPayment = async () => {
    setVerifyingPayment(true);
    try {
      const response = await adminApi.post(`/registrants/${id}/verify-payment`);
      setRegistrant(response.data);
      toast.success("Payment verified — registration confirmed!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify payment");
    } finally {
      setVerifyingPayment(false);
    }
  };

  const rejectPayment = async () => {
    try {
      await adminApi.post(`/registrants/${id}/reject-payment`);
      toast.success("Registration rejected and deleted");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject payment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!registrant) return null;

  const balance = (registrant.amount_due || 0) - (registrant.amount_paid || 0);

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-muted-foreground hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white" data-testid="detail-title">
                Registrant Details
              </h1>
              <p className="text-muted-foreground">
                Registration #{String(registrant.reg_number).padStart(3, "0")}
              </p>
            </div>
          </div>
          
          {/* Edit & Delete Buttons */}
          <div className="flex gap-2">
            {!isEditing ? (
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                className="border-primary/30 text-primary hover:bg-primary/10"
                data-testid="edit-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={cancelEdit}
                  className="border-white/30"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={saveChanges}
                  disabled={saving}
                  className="bg-primary text-black"
                  data-testid="save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save"}
                </Button>
              </>
            )}
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  data-testid="delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-background border-white/10">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Delete Registrant?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete {registrant.name}'s registration. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteRegistrant}
                    className="bg-[#FF0055] hover:bg-[#FF0055]/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* QR Code Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="glass border-white/10 h-full" data-testid="qr-card">
              <CardHeader className="text-center">
                <div className="text-6xl font-black text-primary neon-cyan mb-2" data-testid="detail-reg-number">
                  #{String(registrant.reg_number).padStart(3, "0")}
                </div>
                <p className="text-muted-foreground text-sm">Registration Number</p>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                {/* Payment Verification Block */}
                {registrant.payment_proof_filename && !registrant.proof_verified && (
                  <div className="w-full mb-6 p-4 rounded-lg border-2 border-[#FFC107]/40 bg-[#FFC107]/10 space-y-3" data-testid="pending-verification-block">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#FFC107]" />
                      <p className="text-[#FFC107] font-bold uppercase tracking-wider text-sm">Pending Payment Verification</p>
                    </div>
                    {registrant.expires_at && (
                      <p className="text-xs text-muted-foreground">
                        Auto-deletes at: <span className="text-white">{new Date(registrant.expires_at).toLocaleString()}</span>
                      </p>
                    )}
                    {proofUrl ? (
                      <button
                        type="button"
                        onClick={() => setProofPreviewOpen(true)}
                        className="block w-full"
                        data-testid="view-proof-btn"
                      >
                        <img
                          src={proofUrl}
                          alt="Payment proof"
                          className="w-full max-h-48 object-contain rounded-md border border-white/10 hover:border-primary cursor-zoom-in"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Click to enlarge</p>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <ImageIcon className="w-4 h-4" /> Proof file present (not previewable inline)
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={verifyPayment}
                        disabled={verifyingPayment}
                        className="bg-[#CCFF00] hover:bg-[#CCFF00]/90 text-black font-bold"
                        data-testid="verify-payment-btn"
                      >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        {verifyingPayment ? "Verifying..." : "Verify"}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="destructive"
                            className="bg-[#FF0055] hover:bg-[#FF0055]/90"
                            data-testid="reject-payment-btn"
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-background border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Reject Payment Proof?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete {registrant.name}'s registration and free up their slot. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={rejectPayment}
                              className="bg-[#FF0055] hover:bg-[#FF0055]/90"
                            >
                              Reject & Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                )}

                {registrant.proof_verified && (
                  <div className="w-full mb-6 p-3 rounded-lg border border-[#CCFF00]/30 bg-[#CCFF00]/10 flex items-center gap-2" data-testid="verified-badge-block">
                    <ShieldCheck className="w-5 h-5 text-[#CCFF00]" />
                    <p className="text-[#CCFF00] text-sm font-semibold">Downpayment Verified</p>
                    {proofUrl && (
                      <button
                        type="button"
                        onClick={() => setProofPreviewOpen(true)}
                        className="ml-auto text-xs text-muted-foreground hover:text-primary underline"
                        data-testid="view-verified-proof-btn"
                      >
                        View receipt
                      </button>
                    )}
                  </div>
                )}

                <div className="qr-container mb-6" data-testid="detail-qr-code">
                  <QRCodeSVG
                    value={registrant.id}
                    size={200}
                    level="H"
                    includeMargin={false}
                  />
                </div>

                {/* Status Badges */}
                <div className="flex gap-3 mb-6">
                  <Badge
                    className={`${
                      registrant.payment_status === "paid" ? "status-paid" : "status-unpaid"
                    } px-4 py-2 text-sm uppercase`}
                    data-testid="detail-payment-badge"
                  >
                    {registrant.payment_status === "paid" ? (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    ) : (
                      <XCircle className="w-4 h-4 mr-2" />
                    )}
                    {registrant.payment_status}
                    {registrant.amount_paid > 0 && ` - $${registrant.amount_paid}`}
                  </Badge>
                  <Badge
                    className={`${
                      registrant.collected ? "status-collected" : "bg-muted text-muted-foreground"
                    } px-4 py-2 text-sm uppercase`}
                    data-testid="detail-collected-badge"
                  >
                    {registrant.collected ? "Collected" : "Not Collected"}
                  </Badge>
                </div>

                {/* Payment Summary */}
                <div className="w-full grid grid-cols-3 gap-2 mb-4 text-center">
                  <div className="bg-muted/30 p-3 rounded-lg">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Due</p>
                    <p className="text-white font-bold text-lg">${registrant.amount_due || 0}</p>
                  </div>
                  <div className="bg-[#CCFF00]/10 p-3 rounded-lg">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Paid</p>
                    <p className="text-[#CCFF00] font-bold text-lg">${registrant.amount_paid || 0}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${balance > 0 ? 'bg-[#FF0055]/10' : 'bg-[#CCFF00]/10'}`}>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Balance</p>
                    <p className={`font-bold text-lg ${balance > 0 ? 'text-[#FF0055]' : 'text-[#CCFF00]'}`}>${balance}</p>
                  </div>
                </div>

                {/* Payment Amount Input */}
                <div className="w-full space-y-2 mb-4">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                    Amount Paid ($)
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="amount-paid-input"
                      type="number"
                      placeholder="0.00"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(e.target.value)}
                      className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary text-lg"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="w-full space-y-3">
                  <Button
                    onClick={() => updatePayment(registrant.payment_status === "paid" ? "unpaid" : "paid")}
                    className={`w-full h-12 ${
                      registrant.payment_status === "paid"
                        ? "bg-[#FF0055] hover:bg-[#FF0055]/90"
                        : "bg-[#CCFF00] hover:bg-[#CCFF00]/90 text-black"
                    }`}
                    data-testid="toggle-payment-btn"
                  >
                    {registrant.payment_status === "paid" ? "Mark as Unpaid" : "Mark as Paid"}
                  </Button>
                  
                  {!registrant.collected && (
                    <Button
                      onClick={markCollected}
                      className="w-full h-12 bg-primary hover:bg-primary/90 text-black"
                      data-testid="mark-collected-btn"
                    >
                      <Package className="w-5 h-5 mr-2" />
                      Mark as Collected
                    </Button>
                  )}
                  
                  <Button
                    onClick={resendEmail}
                    disabled={resendingEmail}
                    variant="outline"
                    className="w-full h-12 border-primary/30 text-primary hover:bg-primary/10"
                    data-testid="resend-email-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {resendingEmail ? "Sending..." : "Resend Confirmation Email"}
                  </Button>

                  <Button
                    onClick={resendWhatsapp}
                    disabled={resendingWhatsapp}
                    variant="outline"
                    className="w-full h-12 border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10"
                    data-testid="resend-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {resendingWhatsapp ? "Sending..." : "Send WhatsApp Confirmation"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Details Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="glass border-white/10" data-testid="details-card">
              <CardHeader>
                <CardTitle className="text-white">
                  {isEditing ? "Edit Information" : "Information"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  // Edit Mode
                  <>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <User className="w-4 h-4" /> Name
                      </Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => handleEditChange("name", e.target.value)}
                        className="bg-muted/50 border-transparent"
                        data-testid="edit-name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Contact
                      </Label>
                      <Input
                        value={editForm.contact}
                        onChange={(e) => handleEditChange("contact", e.target.value)}
                        className="bg-muted/50 border-transparent"
                        data-testid="edit-contact"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email
                      </Label>
                      <Input
                        value={editForm.email}
                        onChange={(e) => handleEditChange("email", e.target.value)}
                        className="bg-muted/50 border-transparent"
                        data-testid="edit-email"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <Package className="w-4 h-4" /> Package
                      </Label>
                      <Select
                        value={editForm.package}
                        onValueChange={(value) => handleEditChange("package", value)}
                      >
                        <SelectTrigger className="bg-muted/50 border-transparent" data-testid="edit-package">
                          <SelectValue placeholder="Select package" />
                        </SelectTrigger>
                        <SelectContent>
                          {packages.map((pkg) => (
                            <SelectItem key={pkg.name} value={pkg.name}>
                              {pkg.name} - ${pkg.price}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <Ruler className="w-4 h-4" /> Size
                      </Label>
                      <Select
                        value={editForm.size}
                        onValueChange={(value) => handleEditChange("size", value)}
                      >
                        <SelectTrigger className="bg-muted/50 border-transparent" data-testid="edit-size">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizes.map((size) => (
                            <SelectItem key={size} value={size}>
                              {size}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  // View Mode
                  <>
                    {[
                      { icon: User, label: "Name", value: registrant.name },
                      { icon: Phone, label: "Contact", value: registrant.contact },
                      { icon: Mail, label: "Email", value: registrant.email },
                      { icon: Package, label: "Package", value: registrant.package },
                      { icon: Ruler, label: "Size", value: registrant.size },
                    ].map((item, index) => (
                      <motion.div
                        key={item.label}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">
                            {item.label}
                          </p>
                          <p className="text-white font-medium" data-testid={`detail-${item.label.toLowerCase()}`}>
                            {item.value}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </>
                )}

                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-muted-foreground">
                    Registered on {new Date(registrant.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Proof Preview Lightbox */}
      {proofPreviewOpen && proofUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setProofPreviewOpen(false)}
          data-testid="proof-lightbox"
        >
          <button
            type="button"
            onClick={() => setProofPreviewOpen(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={proofUrl}
            alt="Payment proof full view"
            className="max-h-[90vh] max-w-[95vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
